import { StorageManager } from './storageManager';

/**
 * Storage Migration Manager 
 * THIS HAS ALMOST CERTAINLY RUN FOR >99% OF USERS ALREADY.
 * 
 * This handles a one-time migration from our old localStorage structure to a new, 
 * more organized system. The migration was necessary because:
 * 
 * OLD STRUCTURE PROBLEMS:
 * - Results data was scattered across multiple localStorage keys per challenge
 * - Key naming was inconsistent (yearGuess_completedDaily_, yearGuess_dailyScore_, etc.)
 * - Game state and results were mixed together in confusing ways
 * - Some data was stored redundantly in multiple places
 * 
 * NEW STRUCTURE BENEFITS:
 * - Single results key per challenge with all data in one place
 * - Clear separation between active game state and completed results  
 * - Consistent key naming through StorageManager
 * - Better data integrity and easier debugging
 * 
 * WHAT THIS MIGRATION DOES:
 * 1. Converts old scattered result keys into consolidated results objects
 * 2. Cleans up legacy active game states that were left in "viewing results" mode
 * 3. Fixes corrupted game history records (NaN correctGuesses, missing data)
 * 4. Creates placeholder results data for historical games that lack detailed data
 * 5. Removes all the old fragmented keys to clean up localStorage
 * 
 * This only runs once per user - we set a flag when complete to avoid re-running.
 */

const MIGRATION_FLAG_KEY = 'storageMigration_v2_Complete';

export function runStorageMigrationIfNeeded() {
  // Skip if migration already completed
  if (localStorage.getItem(MIGRATION_FLAG_KEY) === 'true') {
    return;
  }

  let migrationPerformed = false;
  const keysToRemove: string[] = [];
  let currentGameHistory = StorageManager.getGameHistory();
  let historyModified = false;

  // Scan through all localStorage keys to find legacy data
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key) continue;

    try {
      // Clean up old scattered result keys that used inconsistent naming
      if (
        key.startsWith('yearGuess_completedDaily_') ||
        key.startsWith('yearGuess_dailyScore_') ||
        key.startsWith('yearGuess_dailyStats_')
      ) {
        keysToRemove.push(key);
        migrationPerformed = true;
      } 
      // Handle old active game states that got stuck in "viewing results" mode
      else if (key.startsWith(StorageManager.KEYS.ACTIVE_GAME)) {
        const date = key.substring(StorageManager.KEYS.ACTIVE_GAME.length);
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          const rawData = localStorage.getItem(key);
          if (rawData) {
            const oldState = JSON.parse(rawData);
            // If this active state was actually viewing results, convert it to proper results data
            if (oldState && oldState.viewingResults === true) {
              migrationPerformed = true;

              // Only create new results if they don't already exist
              if (
                !localStorage.getItem(StorageManager.KEYS.RESULTS_DATA + date)
              ) {
                // Extract results data from the mixed active state
                const results = oldState.challengeResultData || {};
                const roundData =
                  results.roundData || oldState.allGuesses || [];

                // Create a clean results object with the essential data
                const minimalResults = {
                  score: results.score ?? oldState.score ?? 0,
                  averageYearsOff: results.averageYearsOff ?? 0,
                  roundData: roundData,
                  totalRounds: results.totalRounds ?? roundData.length,
                  dateCompleted: results.dateCompleted || date,
                };
                try {
                  StorageManager.saveResultsData(date, minimalResults);
                  keysToRemove.push(key);
                } catch (saveError) {
                  // Don't remove the old active key if saving the new one failed
                }
              } else {
                keysToRemove.push(key);
              }
              // Clean up related legacy keys
              keysToRemove.push(StorageManager.KEYS.COMPLETED_FLAG + date);
              keysToRemove.push(`yearGuess_dailyScore_${date}`);
              keysToRemove.push(`yearGuess_dailyStats_${date}`);
            }
          }
        }
      }
    } catch (e) {
      // If we can't parse old data, just skip it rather than breaking migration
      // Optionally mark corrupted keys for removal too
      // keysToRemove.push(key);
    }
  }

  // Fix corrupted game history records
  // Some old records ended up with NaN or missing correctGuesses values
  const correctedGameHistory = currentGameHistory
    .map((record) => {
      let updatedRecord = { ...record };
      let recordModified = false;
      if (updatedRecord && typeof updatedRecord.correctGuesses !== 'number') {
        updatedRecord.correctGuesses = 0;
        recordModified = true;
      }
      if (
        updatedRecord &&
        typeof updatedRecord.correctGuesses === 'number' &&
        isNaN(updatedRecord.correctGuesses)
      ) {
        updatedRecord.correctGuesses = 0;
        recordModified = true;
      }
      if (recordModified) {
        historyModified = true;
      }
      return updatedRecord;
    })
    .filter((record) => record && record.date);

  if (historyModified) {
    migrationPerformed = true;
  }

  // Create placeholder results data for any game history entries that lack detailed results
  // This handles cases where we have a history record but lost the detailed round data
  correctedGameHistory.forEach((historyEntry) => {
    if (!historyEntry || !historyEntry.date) return;

    const resultsKey = StorageManager.KEYS.RESULTS_DATA + historyEntry.date;
    if (!localStorage.getItem(resultsKey)) {
      migrationPerformed = true;
      // Create a minimal results object with just the summary data we have
      const minimalResultsData = {
        score: historyEntry.score,
        averageYearsOff: historyEntry.averageYearsOff,
        roundData: [], // No detailed round data available
        imageUrls: [],
        totalRounds: 5,
        correctGuesses: historyEntry.correctGuesses,
        dateCompleted: historyEntry.completionDate || historyEntry.date,
        challengeStats: {
          score: historyEntry.score,
          correctGuesses: historyEntry.correctGuesses,
          totalRounds: 5,
          averageYearsOff: historyEntry.averageYearsOff,
        },
        distributionStats: {
          distributions: [],
          processedDistribution: undefined,
          averageScore: 0,
          completions: 0,
          percentileRank: undefined,
        },
        isPastChallenge: true,
      };
      StorageManager.saveResultsData(historyEntry.date, minimalResultsData);
    }
  });

  // Only proceed with cleanup if we actually migrated something
  if (migrationPerformed || historyModified) {
    try {
      // Save the corrected game history and recalculate user stats
      localStorage.setItem(
        StorageManager.KEYS.GAME_HISTORY,
        JSON.stringify(correctedGameHistory)
      );
      const finalStats =
        StorageManager._calculateStatsFromHistory(correctedGameHistory);
      localStorage.setItem(
        StorageManager.KEYS.USER_STATS,
        JSON.stringify(finalStats)
      );

      // Remove all the old fragmented keys to clean up localStorage
      const uniqueKeysToRemove = Array.from(new Set(keysToRemove));
      if (uniqueKeysToRemove.length > 0) {
        uniqueKeysToRemove.forEach((key) => localStorage.removeItem(key));
      }
    } catch (finalSaveError) {
      throw new Error('Migration failed during final save/cleanup phase');
    }
    // Only set the migration flag if something was actually migrated or cleaned up
    localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
  }
}
